import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Pencil, Clock, Paperclip, Search, Plus, Loader2, Trash2, Check, X } from 'lucide-react';
import { api, type CaptureResponse } from '../lib/api';
import { listMine, forgetCapture } from '../lib/myCaptures';
import { usePanelOpen } from '../lib/usePanelOpen';

const PAGE = 10; // captures chargées par lot (pagination « voir plus »).

/**
 * « Tes captures » : les grains capturés depuis ce compte, rouvrables pour
 * édition (CAP-05). Pour les gros volumes (100-200 captures) : recherche par
 * mot-clé + pagination « voir plus » par lot de dix, côté serveur. Repli sur le
 * navigateur (mode ouvert sans connexion) sans pagination. Ouvert par défaut.
 */
export function MyCaptures({
  onEdit,
  onDeleted,
  refreshKey,
}: {
  onEdit: (n: CaptureResponse) => void;
  onDeleted?: (id: string) => void;
  refreshKey: number;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = usePanelOpen('gnosis.panel.mine', true);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState(''); // requête débouncée
  const [items, setItems] = useState<CaptureResponse[]>([]);
  const [total, setTotal] = useState(0); // total de la requête courante (filtré)
  const [baseTotal, setBaseTotal] = useState(0); // total non filtré → affordances UI
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false); // chargement d'un lot supplémentaire
  const fallback = useRef(false); // repli navigateur : tout est déjà chargé, pas de pagination

  // Débounce de la frappe (évite un appel par caractère).
  useEffect(() => {
    const h = setTimeout(() => setDq(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  // (Re)chargement page 0 à chaque changement de requête ou de rafraîchissement.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    void (async () => {
      const res = await api.myNotes({ q: dq, limit: PAGE, offset: 0 }).catch(() => null);
      if (!alive) return;
      if (res && (res.total > 0 || dq)) {
        fallback.current = false;
        setItems(res.items);
        setTotal(res.total);
        if (!dq) setBaseTotal(res.total);
      } else {
        // Repli navigateur (mode ouvert) : on charge les ids locaux, filtrés côté client.
        const refs = listMine();
        let found: CaptureResponse[] = [];
        if (refs.length) {
          found = await api.mine(refs.map((r) => r.id)).catch(() => []);
          const present = new Set(found.map((n) => n.id));
          refs.forEach((r) => present.has(r.id) || forgetCapture(r.id));
        }
        if (!alive) return;
        fallback.current = true;
        const needle = dq.toLowerCase();
        const filtered = dq ? found.filter((n) => `${n.title} ${n.bodyClean}`.toLowerCase().includes(needle)) : found;
        setItems(filtered);
        setTotal(filtered.length);
        if (!dq) setBaseTotal(found.length);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [dq, refreshKey]);

  const loadMore = async (): Promise<void> => {
    if (fallback.current) return;
    setMore(true);
    const res = await api.myNotes({ q: dq, limit: PAGE, offset: items.length }).catch(() => null);
    if (res) {
      setItems((prev) => [...prev, ...res.items]);
      setTotal(res.total);
    }
    setMore(false);
  };

  // Suppression d'une capture (CAP-07) : retire la ligne et décrémente les totaux.
  const remove = async (n: CaptureResponse): Promise<void> => {
    setDeleting(n.id);
    try {
      await api.deleteNote(n.id);
      forgetCapture(n.id);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      setTotal((x) => Math.max(0, x - 1));
      setBaseTotal((x) => Math.max(0, x - 1));
      onDeleted?.(n.id);
    } catch {
      /* on laisse l'utilisateur réessayer */
    }
    setDeleting(null);
    setConfirmId(null);
  };

  // Rien capturé : panneau masqué (tant qu'on ne filtre pas).
  if (baseTotal === 0 && !dq) return null;

  const fmtDate = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString(i18n.language === 'de' ? 'de-CH' : 'fr-CH', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Recherche toujours visible dès qu'il y a des captures (utile même sur une petite liste).
  const searchable = baseTotal > 0 || dq.length > 0;
  const remaining = total - items.length;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="group mb-2 rounded-xl bg-accent/5 px-3 py-2 ring-1 ring-accent/20"
    >
      <summary className="flex cursor-pointer list-none items-center gap-1 text-xs font-semibold text-fg">
        <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
        {t('capture.mine')} · {baseTotal}
      </summary>

      {searchable && (
        <div className="relative mt-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('capture.search')}
            className="w-full rounded-lg bg-surface py-1.5 pl-8 pr-3 text-sm ring-1 ring-border outline-none placeholder:text-muted focus:ring-accent"
          />
        </div>
      )}

      {loading ? (
        <p className="mt-2 px-2 py-1.5 text-xs text-muted">{t('explore.loading')}</p>
      ) : items.length === 0 ? (
        <p className="mt-2 px-2 py-1.5 text-xs text-muted">{t('capture.noMatch', { q: dq })}</p>
      ) : (
        <>
          <ul className="mt-2 space-y-1">
            {items.map((n) => (
              <li key={n.id}>
                <div className="flex items-start gap-1">
                  <button
                    onClick={() => onEdit(n)}
                    className="flex min-w-0 flex-1 items-start gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-surface"
                  >
                    <Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{n.title}</span>
                        {n.status === 'PENDING_ENRICH' && (
                          <span className="flex shrink-0 items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                            <Clock className="h-3 w-3" /> {t('capture.pendingShort')}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                        {n.createdAt && <span>{fmtDate(n.createdAt)}</span>}
                        {n.themes.slice(0, 3).map((th) => (
                          <span key={th.theme.label} className="text-accent">
                            #{th.theme.label}
                          </span>
                        ))}
                        {n.attachments && n.attachments.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3" /> {t('capture.attachments', { count: n.attachments.length })}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  {/* Suppression (CAP-07) : confirmation en deux temps, inline. */}
                  {confirmId === n.id ? (
                    <span className="mt-1 flex shrink-0 items-center gap-0.5">
                      <button
                        onClick={() => remove(n)}
                        disabled={deleting === n.id}
                        aria-label={t('capture.deleteYes')}
                        title={t('capture.deleteYes')}
                        className="grid h-6 w-6 place-items-center rounded text-red-600 hover:bg-red-500/10 disabled:opacity-60 dark:text-red-400"
                      >
                        {deleting === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        aria-label={t('capture.deleteNo')}
                        title={t('capture.deleteNo')}
                        className="grid h-6 w-6 place-items-center rounded text-muted hover:bg-surface hover:text-fg"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmId(n.id)}
                      aria-label={t('capture.delete')}
                      title={t('capture.delete')}
                      className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded text-muted/60 transition hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {n.attachments && n.attachments.length > 0 && (
                  <div className="ml-7 mt-0.5 flex flex-wrap gap-1.5 pb-1">
                    {n.attachments.map((a) => (
                      <a
                        key={a.id}
                        href={api.attachmentUrl(n.id, a.id)}
                        target="_blank"
                        rel="noreferrer"
                        title={t('capture.openAttachment')}
                        className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-1.5 py-0.5 text-xs text-muted hover:text-fg"
                      >
                        <Paperclip className="h-3 w-3" /> <span className="max-w-[10rem] truncate">{a.filename}</span>
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {remaining > 0 && !fallback.current && (
            <button
              onClick={loadMore}
              disabled={more}
              className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-accent hover:bg-surface disabled:opacity-60"
            >
              {more ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {t('capture.more', { count: Math.min(PAGE, remaining) })}
            </button>
          )}
        </>
      )}
    </details>
  );
}
